import type { FC, PropsWithChildren } from 'react';
import { useRouter } from 'next/router';

import { ArrowMediumDown32, ArrowMediumRight32 } from '@packages/themes/icons';
import {
  ListItemText,
  MenuList,
  MenuItem,
  Typography,
  Box,
  Link,
  ListItemIcon,
  mergeQueryParameters,
} from '@packages/shared';
import { useMouseflow } from '@packages/tracking';

import type { CategoryNavigationItem } from '../../api/rest/types';
import { extractDisplayMode, extractOrder, extractSelectedFilterValues } from '../../queryEncoding';

/** Displays Content of nested menu item.
 *  NOTE: Level starts with 2 for Category navigation.
 *  Level 2 should display the arrow icon, if it has children.
 *  - Arrow down when children are expanded
 *  - Arrow right when children are collapsed
 *  Level 3 and above should display a plus or minus sign, if it has children.
 *  - Plus sign when children are collapsed
 *  - Minus sign when children are expanded
 *  If there are no children no icon should be displayed */
const CategoryItemContent = ({
  category,
  children,
}: PropsWithChildren<{ category: CategoryNavigationItem }>) =>
  category.level === 2 ? (
    <>
      {children}
      {(category.hasChildren || category.children?.length) && (
        <ListItemIcon>
          {category.isSelected ? <ArrowMediumDown32 /> : <ArrowMediumRight32 />}
        </ListItemIcon>
      )}
    </>
  ) : (
    <>
      {category.hasChildren || category.children?.length ? (
        <Typography variant="h5" component="span" sx={{ marginRight: 1, marginLeft: 0.5 }}>
          {category.isSelected ? '-' : '+'}
        </Typography>
      ) : (
        <Box sx={{ marginRight: 2.3 }} />
      )}
      {children}
    </>
  );

/** Displays the name of the category */
const CategoryItemText = ({ category }: { category: CategoryNavigationItem }) => (
  <ListItemText
    primaryTypographyProps={{
      noWrap: true,
      title: category.name,
      sx: { fontWeight: category.isSelected ? 'bold' : 'inherit' },
    }}
  >
    {category.name}
  </ListItemText>
);

type CategoryMenuItemProps = {
  category: CategoryNavigationItem;
  isChildOfActiveCategory?: boolean;
};

/** Displays MenuItem for every nested child through recursion */
const CategoryMenuItem: FC<CategoryMenuItemProps> = ({ category, isChildOfActiveCategory }) => {
  const { query } = useRouter();
  const pushMouseflowData = useMouseflow();

  const href = isChildOfActiveCategory
    ? mergeQueryParameters(category.uriPath, {
        ...extractDisplayMode(query),
        ...extractOrder(query),
        ...extractSelectedFilterValues(query),
      })
    : category.uriPath;

  return (
    <MenuItem
      dense={category.level > 2}
      sx={{
        paddingLeft: category.level > 3 ? category.level - 2 : 1,
      }}
      component={Link}
      href={href}
      onClick={() => {
        pushMouseflowData('search_NaviLeft');
      }}
    >
      <CategoryItemContent category={category}>
        <CategoryItemText category={category} />
      </CategoryItemContent>
    </MenuItem>
  );
};

export type ProductCategoryNavigationDesktopProps = {
  /**
   * All categories that should be displayed in the navigation
   */
  items: CategoryNavigationItem[];
};

const flattenChildren = (items: CategoryNavigationItem[], item: CategoryNavigationItem) => {
  items.push(item);
  if (item.children) {
    items.push(...item.children.reduce(flattenChildren, []));
  }

  return items;
};

/**
 * Extracts all direct children of the active category
 *
 * @param items flat list of all categories
 */
const getChildrenOfActiveCategory = (items: CategoryNavigationItem[]) => {
  let activeCategoryIndex = -1;
  let i = items.length - 1;
  while (i >= 0) {
    if (items[i].isSelected) {
      activeCategoryIndex = i;
      break;
    }
    i -= 1;
  }

  const activeLevel = items[activeCategoryIndex]?.level;

  const childrenOfActiveCategory: CategoryNavigationItem[] = [];
  for (const item of items.slice(activeCategoryIndex + 1)) {
    if (item.level <= activeLevel) break;

    childrenOfActiveCategory.push(item);
  }

  return childrenOfActiveCategory;
};

/**
 * Displays the category navigation in desktop view
 * */
export const ProductCategoryNavigationDesktop: FC<ProductCategoryNavigationDesktopProps> = ({
  items,
}) => {
  const flatItems = items.reduce(flattenChildren, []);

  const childrenOfActiveCategory = getChildrenOfActiveCategory(flatItems);

  return (
    <MenuList data-testid="product-category-navigation-desktop" sx={{ paddingBottom: 0 }}>
      {flatItems.map((item) => (
        <CategoryMenuItem
          key={item.id}
          category={item}
          isChildOfActiveCategory={childrenOfActiveCategory.includes(item)}
        />
      ))}
    </MenuList>
  );
};
